import { useState, useRef, useEffect, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { getCsrfToken } from '@/lib/csrf';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { AuthFormProps } from './types';
import { ArrowLeft, Mail, Eye, EyeOff, Loader2, CheckCircle, Clock, RefreshCw, Smartphone, QrCode } from 'lucide-react';
import { PasswordStrengthMeter } from './SignUpForm';
import echo from '@/echo';

type FormView = 'login' | 'forgot-password' | 'forgot-password-sent' | 'reset-password-new';

// ============================================
// QR TYPES
// ============================================
interface QrTokenData {
  success: boolean;
  token: string;
  qr_url: string;
  expires_at: string;
  expires_in_seconds: number;
}

interface StatusResponse {
  success: boolean;
  status: 'pending' | 'approved' | 'used' | 'expired';
  expires_in_seconds: number;
  user_id?: number;
}

type QrStatus = 'idle' | 'loading' | 'ready' | 'approved' | 'expired' | 'error';

// ============================================
// PROPS
// ============================================
interface LoginFormProps extends AuthFormProps {
  fieldErrors?: Record<string, string>;
}

// ============================================
// SHARED INPUT STYLES - Vercel/Linear aesthetic
// ============================================
const inputBase = 'h-11 w-full rounded-lg border bg-white dark:bg-[#0a0a0a] px-3.5 text-[14px] text-[#0a0a0a] dark:text-white placeholder:text-[#999] dark:placeholder:text-[#555] transition-colors duration-150 outline-none';
const inputDefault = 'border-[#e5e5e5] dark:border-[#333] hover:border-[#ccc] dark:hover:border-[#444] focus:border-[#1bb8f5] dark:focus:border-[#1bb8f5] focus:ring-2 focus:ring-[#1bb8f5]/10';
const inputError = 'border-red-400 dark:border-red-600 hover:border-red-500 focus:border-red-500 focus:ring-0';

// ============================================
// MAIN FORM
// ============================================
export const LoginForm = ({ isLoading, onSubmit, fieldErrors = {} }: LoginFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [view, setView] = useState<FormView>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ email, password, remember });
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsResetting(false);
    setView('forgot-password-sent');
  };

  return (
    <AnimatePresence mode="wait">
      {/* ====== LOGIN ====== */}
      {view === 'login' && (
        <motion.div
          key="login"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* E-posta */}
            <div className="flex flex-col gap-2">
              <label htmlFor="login-email" className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
                E-posta
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="ornek@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className={`${inputBase} ${fieldErrors.email ? inputError : inputDefault}`}
              />
              <FieldError message={fieldErrors.email} />
            </div>

            {/* Sifre */}
            <div className="flex flex-col gap-2">
              <label htmlFor="login-password" className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
                Şifre
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className={`${inputBase} pr-10 ${fieldErrors.password ? inputError : inputDefault}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#666] dark:text-[#555] dark:hover:text-[#888] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
                </button>
              </div>
              <FieldError message={fieldErrors.password} />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setView('forgot-password')}
                  className="text-[12px] text-[#1bb8f5] hover:text-[#0ea5d6] dark:text-[#1bb8f5] dark:hover:text-[#4fcaff] transition-colors"
                >
                  Şifremi Unuttum
                </button>
              </div>
            </div>

            {/* Remember */}
            <label className="flex cursor-pointer items-center gap-2.5 select-none group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="h-[18px] w-[18px] rounded-[5px] border border-[#d4d4d4] dark:border-[#444] bg-white dark:bg-[#0a0a0a] transition-all peer-checked:border-[#1bb8f5] peer-checked:bg-[#1bb8f5] dark:peer-checked:border-[#1bb8f5] dark:peer-checked:bg-[#1bb8f5] peer-focus-visible:ring-2 peer-focus-visible:ring-[#1bb8f5]/20" />
                <svg
                  className="absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-[13px] text-[#666] dark:text-[#888] group-hover:text-[#0a0a0a] dark:group-hover:text-[#ccc] transition-colors">
                Oturumumu açık tut
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="relative mt-1 flex h-11 w-full items-center justify-center rounded-lg bg-[#1bb8f5] text-[14px] font-medium text-white transition-all duration-150 hover:bg-[#0ea5d6] active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none shadow-sm shadow-[#1bb8f5]/20"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  <span>Giriş Yapılıyor...</span>
                </div>
              ) : (
                <span>Giriş Yap</span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#e5e5e5] dark:border-[#333]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#fafafa] dark:bg-[#0a0a0a] px-3 text-[11px] uppercase tracking-widest text-[#999] dark:text-[#555]">
                veya
              </span>
            </div>
          </div>

          {/* QR Login */}
          <QrLoginInline />
        </motion.div>
      )}

      {/* ====== FORGOT PASSWORD ====== */}
      {view === 'forgot-password' && (
        <motion.form
          key="forgot-password"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={handleForgotPassword}
          className="flex flex-col gap-5"
        >
          <button
            type="button"
            onClick={() => setView('login')}
            className="flex items-center gap-1.5 text-[13px] text-[#666] dark:text-[#888] hover:text-[#1bb8f5] dark:hover:text-[#1bb8f5] transition-colors w-fit"
          >
            <ArrowLeft className="size-3.5" />
            Geri dön
          </button>

          <div>
            <h3 className="text-[18px] font-semibold text-[#0a0a0a] dark:text-white tracking-tight">
              Şifreni Sıfırla
            </h3>
            <p className="mt-1.5 text-[13px] text-[#666] dark:text-[#888] leading-relaxed">
              E-posta adresini gir, sana sıfırlama bağlantısı gönderelim.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="reset-email" className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
              E-posta
            </label>
            <input
              id="reset-email"
              type="email"
              placeholder="ornek@mail.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              autoComplete="email"
              required
              className={`${inputBase} ${inputDefault}`}
            />
          </div>

          <button
            type="submit"
            disabled={isResetting || !resetEmail}
            className="flex h-11 w-full items-center justify-center rounded-lg bg-[#1bb8f5] text-[14px] font-medium text-white transition-all duration-150 hover:bg-[#0ea5d6] active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none shadow-sm shadow-[#1bb8f5]/20"
          >
            {isResetting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                <span>Gönderiliyor...</span>
              </div>
            ) : (
              <span>Sıfırlama Bağlantısı Gönder</span>
            )}
          </button>
        </motion.form>
      )}

      {/* ====== FORGOT PASSWORD SENT ====== */}
      {view === 'forgot-password-sent' && (
        <motion.div
          key="forgot-password-sent"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col items-center gap-4 py-6"
        >
          <div className="flex size-14 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/20">
            <CheckCircle className="size-7 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
          </div>
          <div className="text-center">
            <h3 className="text-[18px] font-semibold text-[#0a0a0a] dark:text-white tracking-tight">
              E-posta Gönderildi
            </h3>
            <p className="mt-2 text-[13px] text-[#666] dark:text-[#888] leading-relaxed">
              <span className="font-medium text-[#0a0a0a] dark:text-white">{resetEmail}</span> adresine
              sıfırlama bağlantısı gönderildi. Spam klasörünü de kontrol et.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setView('login'); setResetEmail(''); }}
            className="mt-2 flex h-11 w-full items-center justify-center rounded-lg bg-[#1bb8f5] text-[14px] font-medium text-white transition-all duration-150 hover:bg-[#0ea5d6] active:scale-[0.98] shadow-sm shadow-[#1bb8f5]/20"
          >
            Giriş Ekranına Dön
          </button>
        </motion.div>
      )}

      {/* ====== RESET PASSWORD NEW ====== */}
      {view === 'reset-password-new' && (
        <motion.form
          key="reset-password-new"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={(e) => {
            e.preventDefault();
            setIsResetting(true);
            setTimeout(() => {
              setIsResetting(false);
              setView('login');
              setNewPassword('');
              setNewPasswordConfirm('');
            }, 1500);
          }}
          className="flex flex-col gap-5"
        >
          <button
            type="button"
            onClick={() => setView('forgot-password-sent')}
            className="flex items-center gap-1.5 text-[13px] text-[#666] dark:text-[#888] hover:text-[#1bb8f5] dark:hover:text-[#1bb8f5] transition-colors w-fit"
          >
            <ArrowLeft className="size-3.5" />
            Geri Dön
          </button>

          <div>
            <h3 className="text-[18px] font-semibold text-[#0a0a0a] dark:text-white tracking-tight">
              Yeni Şifre Belirle
            </h3>
            <p className="mt-1.5 text-[13px] text-[#666] dark:text-[#888]">
              Hesabın için yeni bir şifre belirle.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="new-password" className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`${inputBase} pr-10 ${inputDefault}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#666] dark:text-[#555] dark:hover:text-[#888] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
              </button>
            </div>
            <PasswordStrengthMeter password={newPassword} />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="new-password-confirm" className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
              Şifre Tekrar
            </label>
            <input
              id="new-password-confirm"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              className={`${inputBase} ${
                newPasswordConfirm
                  ? newPassword === newPasswordConfirm
                    ? 'border-emerald-400 dark:border-emerald-600 focus:border-emerald-500 focus:ring-0'
                    : inputError
                  : inputDefault
              }`}
            />
            {newPasswordConfirm && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`text-[12px] ${
                  newPassword === newPasswordConfirm
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-500 dark:text-red-400'
                }`}
              >
                {newPassword === newPasswordConfirm ? 'Şifreler uyuşuyor' : 'Şifreler eşleşmiyor'}
              </motion.p>
            )}
          </div>

          <button
            type="submit"
            disabled={isResetting || !newPassword || !newPasswordConfirm || newPassword !== newPasswordConfirm}
            className="flex h-11 w-full items-center justify-center rounded-lg bg-[#1bb8f5] text-[14px] font-medium text-white transition-all duration-150 hover:bg-[#0ea5d6] active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none shadow-sm shadow-[#1bb8f5]/20"
          >
            {isResetting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                <span>Güncelleniyor...</span>
              </div>
            ) : (
              'Şifreyi Güncelle'
            )}
          </button>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

// ============================================
// FIELD ERROR
// ============================================
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-[12px] text-red-500 dark:text-red-400"
    >
      {message}
    </motion.p>
  );
}

// ============================================
// QR LOGIN INLINE
// ============================================
function QrLoginInline() {
  const [tokenData, setTokenData] = useState<QrTokenData | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [status, setStatus] = useState<QrStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [approvedUserName, setApprovedUserName] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const safeJson = async (response: Response) => {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error('Sunucudan geçersiz yanıt alındı');
    }
    return response.json();
  };

  const generateToken = useCallback(async () => {
    setStatus('loading');
    setError(null);
    setApprovedUserName(null);

    try {
      const response = await fetch(route('qr-login.generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken(),
        },
      });

      const data = await safeJson(response);
      if (!response.ok) throw new Error(data.message || 'Token oluşturulamadı');

      setTokenData(data);
      setRemainingSeconds(data.expires_in_seconds);
      setStatus('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
      setStatus('error');
    }
  }, []);

  const completeLogin = useCallback(async (token: string) => {
    try {
      const response = await fetch(route('qr-login.complete'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken(),
        },
        body: JSON.stringify({ token }),
      });
      const data = await safeJson(response);
      if (data.success && data.redirect) router.visit(data.redirect);
    } catch {
      setError('Oturum tamamlanırken hata oluştu');
      setStatus('error');
    }
  }, []);

  const checkStatus = useCallback(async (token: string) => {
    try {
      const response = await fetch(route('qr-login.status', { token }), {
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) return;
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;
      const data: StatusResponse = await response.json();

      if (data.status === 'approved') {
        if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
        setStatus('approved');
        setTimeout(() => completeLogin(token), 1500);
      } else if (data.status === 'expired') {
        if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
        setStatus('expired');
      }
    } catch { /* silent */ }
  }, [completeLogin]);

  useEffect(() => {
    if (!tokenData?.token || status !== 'ready') return;
    let channel: ReturnType<typeof echo.channel> | null = null;
    try {
      if (!echo) return;
      channel = echo.channel(`qr-login.${tokenData.token}`);
      channel
        .subscribed(() => { if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; } })
        .listen('.login.approved', (event: { status: string; token: string; user_name?: string }) => {
          if (event.status === 'approved') {
            setStatus('approved');
            setApprovedUserName(event.user_name || null);
            setTimeout(() => completeLogin(tokenData.token), 1500);
          }
        });
    } catch { /* WebSocket failed */ }
    return () => { if (channel && echo) { try { echo.leave(`qr-login.${tokenData.token}`); } catch { /* ignore */ } } };
  }, [tokenData?.token, status, completeLogin]);

  useEffect(() => {
    if (!tokenData?.token || status !== 'ready') return;
    checkStatus(tokenData.token);
    pollingIntervalRef.current = setInterval(() => { if (status === 'ready') checkStatus(tokenData.token); }, 2000);
    return () => { if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; } };
  }, [tokenData?.token, status, checkStatus]);

  useEffect(() => {
    if (status !== 'ready' || remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => { if (prev <= 1) { setStatus('expired'); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(interval);
  }, [status, remainingSeconds]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // IDLE
  if (status === 'idle') {
    return (
      <button
        type="button"
        onClick={generateToken}
        className="flex w-full items-center gap-3.5 rounded-lg border border-[#e5e5e5] dark:border-[#333] bg-white dark:bg-[#0a0a0a] p-3.5 transition-all duration-150 hover:border-[#ccc] dark:hover:border-[#444] hover:bg-[#fafafa] dark:hover:bg-[#111] active:scale-[0.99] cursor-pointer"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1bb8f5]/10 dark:bg-[#1bb8f5]/10">
          <QrCode className="h-5 w-5 text-[#1bb8f5]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 text-left">
          <p className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">QR Kod ile Giriş</p>
          <p className="text-[11px] text-[#999] dark:text-[#555] mt-0.5">
            Telefonunuzla okutarak giriş yapın
          </p>
        </div>
        <svg className="size-4 text-[#ccc] dark:text-[#444]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    );
  }

  // LOADING
  if (status === 'loading') {
    return (
      <div className="flex items-center gap-3.5 rounded-lg border border-[#e5e5e5] dark:border-[#333] bg-white dark:bg-[#0a0a0a] p-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f5f5f5] dark:bg-[#1a1a1a]">
          <Loader2 className="h-4 w-4 animate-spin text-[#999]" />
        </div>
        <p className="text-[13px] text-[#999] dark:text-[#666]">QR kod oluşturuluyor...</p>
      </div>
    );
  }

  // ERROR
  if (status === 'error') {
    return (
      <div className="flex items-center gap-3.5 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 p-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20">
          <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="flex-1 text-[13px] text-red-600 dark:text-red-400">{error}</p>
        <button type="button" onClick={generateToken} className="text-[12px] font-medium text-red-600 hover:text-red-700 dark:text-red-400 shrink-0">
          Tekrar Dene
        </button>
      </div>
    );
  }

  // APPROVED
  if (status === 'approved') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3.5 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-900/10 p-3.5"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 250, damping: 20 }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/20"
        >
          <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
        </motion.div>
        <div className="flex-1">
          <p className="text-[13px] font-medium text-emerald-700 dark:text-emerald-400">Giriş Onaylandı</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Loader2 className="h-3 w-3 animate-spin text-emerald-600 dark:text-emerald-400" />
            <p className="text-[11px] text-emerald-600/70 dark:text-emerald-400/70">
              {approvedUserName ? `Hoş geldin ${approvedUserName}` : 'Yönlendiriliyorsunuz...'}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // EXPIRED
  if (status === 'expired') {
    return (
      <div className="flex items-center gap-3.5 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-900/10 p-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/20">
          <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
        </div>
        <p className="flex-1 text-[13px] text-amber-700 dark:text-amber-400">Süre doldu</p>
        <button
          type="button"
          onClick={generateToken}
          className="flex items-center gap-1.5 rounded-md bg-amber-100 dark:bg-amber-900/20 px-2.5 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/30 transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Yenile
        </button>
      </div>
    );
  }

  // READY
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex items-start gap-4 rounded-lg border border-[#e5e5e5] dark:border-[#333] bg-white dark:bg-[#0a0a0a] p-4"
    >
      {/* QR */}
      <div className="shrink-0">
        <div className="rounded-lg bg-white p-2 ring-1 ring-[#e5e5e5] dark:ring-[#333]">
          <QRCodeSVG
            value={tokenData?.qr_url || ''}
            size={112}
            level="H"
            includeMargin={false}
            imageSettings={{
              src: '/oa-logo-svg1.svg',
              height: 20,
              width: 20,
              excavate: true,
            }}
          />
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 py-0.5">
        <p className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed] mb-2.5">
          Telefonunuzla okutun
        </p>

        <ol className="space-y-2 text-[12px] text-[#666] dark:text-[#888]">
          <li className="flex items-center gap-2">
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#1bb8f5]/10 text-[10px] font-semibold text-[#1bb8f5]">1</span>
            Telefonunuzda giriş yapın
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#1bb8f5]/10 text-[10px] font-semibold text-[#1bb8f5]">2</span>
            Bu QR kodu tarayın
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#1bb8f5]/10 text-[10px] font-semibold text-[#1bb8f5]">3</span>
            Girişi onaylayın
          </li>
        </ol>

        {/* Timer */}
        <div className="mt-3 flex items-center gap-2">
          <span className="font-mono text-[11px] font-medium text-[#999] dark:text-[#666]">{formatTime(remainingSeconds)}</span>
          <div className="flex-1 h-[3px] rounded-full bg-[#f0f0f0] dark:bg-[#1a1a1a] overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-[#1bb8f5]"
              initial={{ width: '100%' }}
              animate={{ width: `${tokenData ? (remainingSeconds / 300) * 100 : 0}%` }}
              transition={{ duration: 0.5, ease: 'linear' }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
